import styles from './listingQuality.module.scss'
import { getScoreBandClass } from './scoreBand'

import Typography from 'Clutch/Atoms/Typography/Typography'

// Products re-checks the flag and redirects, so the internal dashboard host never reaches this bundle.
const LISTING_QUALITY_DASHBOARD_ENDPOINT = '/api/products/listingquality/dashboard'

const buildDashboardUrl = (brandId, skuVariantNumber) => {
  const params = new URLSearchParams({ skuVariantNumber: skuVariantNumber ?? '' })
  if (brandId != null) params.set('brandId', brandId)

  return `${LISTING_QUALITY_DASHBOARD_ENDPOINT}?${params}`
}

// A rule with no matching definition is skipped rather than falling back to its raw id — an
// unresolved id is noise to the reader, not a finding.
const groupFailedRulesByCategory = (failedRules, ruleDefinitions) => {
  const definitionsByRuleId = new Map((ruleDefinitions || []).map((definition) => [definition.ruleId, definition]))

  return (failedRules || []).reduce((groups, rule) => {
    const definition = definitionsByRuleId.get(rule.ruleId)
    if (!definition?.category || !definition?.displayName) return groups

    if (!groups[definition.category]) groups[definition.category] = []
    groups[definition.category].push({ ruleId: rule.ruleId, displayName: definition.displayName })

    return groups
  }, {})
}

const ListingQualityModal = ({ score, ruleDefinitions }) => {
  if (!score) return false

  const overallScore = score.overallScore ?? score.score
  const categoryScores = score.categoryScores || []
  const failedRules = score.failedRules || []
  const recommendations = [...(score.recommendations || [])].sort(
    (a, b) => (b.potentialScoreLift ?? 0) - (a.potentialScoreLift ?? 0),
  )
  const groupedFailedRules = groupFailedRulesByCategory(failedRules, ruleDefinitions)

  const openDashboard = () =>
    window.open(buildDashboardUrl(score.brandId, score.skuVariantNumber), '_blank', 'noopener,noreferrer')

  return (
    <div className={styles.modalWrapper}>
      <div className={styles.modalHeader}>
        <Typography font={'bold'} as={'h2'} brand={'primary'}>
          Listing Quality
        </Typography>
        <div className={styles.modalHeaderScore}>
          <Typography size={2} font={'bold'}>
            {Math.round(overallScore)}
          </Typography>
          {score.scoreBand && (
            <span className={`${styles.bandBadge} ${getScoreBandClass(score.scoreBand)}`}>
              <Typography size={0.75} font={'bold'}>
                {score.scoreBand}
              </Typography>
            </span>
          )}
        </div>
        {score.calculatedUtc && (
          <Typography size={0.75} className={styles.calculatedAt}>
            Scored {new Date(score.calculatedUtc).toLocaleString()}
          </Typography>
        )}
      </div>

      {!!categoryScores.length && (
        <section className={styles.modalSection}>
          <Typography font={'bold'} as={'h3'}>
            Category Scores
          </Typography>
          <ul className={styles.categoryGrid}>
            {categoryScores.map((category) => (
              <li className={styles.categoryRow} key={`listing-quality-category-${category.categoryName}`}>
                <Typography size={1}>{category.categoryName}</Typography>
                <span
                  className={`${styles.categoryScore} ${getScoreBandClass(category.scoreBand)}`}
                  aria-label={`${category.categoryName}: ${Math.round(category.score)}${
                    category.scoreBand ? `, ${category.scoreBand}` : ''
                  }`}
                >
                  <Typography size={1} font={'bold'}>
                    {Math.round(category.score)}
                  </Typography>
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {!!recommendations.length && (
        <section className={styles.modalSection}>
          <Typography font={'bold'} as={'h3'}>
            Recommendations
          </Typography>
          <ul>
            {recommendations.map((recommendation, index) => (
              <li key={`listing-quality-recommendation-${index}`}>
                <Typography size={1}>
                  {recommendation.recommendation}
                  {recommendation.potentialScoreLift ? ` (+${recommendation.potentialScoreLift})` : ''}
                </Typography>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className={styles.modalSection}>
        <Typography font={'bold'} as={'h3'}>
          Broken Rules
        </Typography>
        {!failedRules.length && <Typography size={1}>No failed rules</Typography>}
        {Object.entries(groupedFailedRules).map(([category, rules]) => (
          <div className={styles.ruleGroup} key={`listing-quality-rule-group-${category}`}>
            <Typography size={1} font={'bold'}>
              {category}
            </Typography>
            <ul>
              {rules.map((rule) => (
                <li key={`listing-quality-rule-${rule.ruleId}`}>
                  <Typography size={1}>{rule.displayName}</Typography>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </section>

      <button type={'button'} className={styles.dashboardButton} onClick={openDashboard}>
        <Typography size={1}>Open in Listing Quality dashboard</Typography>
      </button>
    </div>
  )
}

export default ListingQualityModal
